import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Banknote, Lock, Unlock, AlertTriangle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TILL_CHANGED_EVENT, fmtNZTime, fmtHours } from '../hooks/useTillStatus';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const VarianceBadge = ({ v }) => {
  const n = Number(v || 0);
  if (Math.abs(n) < 0.01) return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Balanced</Badge>;
  return n > 0
    ? <Badge className="bg-blue-100 text-blue-800 border-blue-200" variant="outline">{money(n)} over</Badge>
    : <Badge className="bg-red-100 text-red-800 border-red-200" variant="outline">{money(Math.abs(n))} short</Badge>;
};

const StoreTill = ({ s }) => {
  const last = s.closed_today?.[0];
  const headline = {
    before_open: { icon: Clock, cls: 'text-slate-500', text: `Opens ${fmtHours(s.hours.open)}` },
    needs_open: { icon: AlertTriangle, cls: 'text-red-600', text: 'Not opened yet' },
    missed: { icon: AlertTriangle, cls: 'text-red-600', text: 'Till was not opened today' },
    stale: { icon: AlertTriangle, cls: 'text-red-700', text: `Still open from ${s.opened_on}` },
    open: { icon: Unlock, cls: 'text-emerald-700', text: `Open since ${fmtNZTime(s.opened_at)} · ${s.opened_by}` },
    needs_close: { icon: Lock, cls: 'text-amber-700', text: `Open since ${fmtNZTime(s.opened_at)} · ${s.opened_by} — closing time` },
    done_today: { icon: Lock, cls: 'text-slate-700', text: last ? `Closed ${fmtNZTime(last.closed_at)} · ${last.closed_by}` : 'Closed' },
  }[s.phase] || { icon: Clock, cls: 'text-slate-500', text: s.phase };
  const Icon = headline.icon;

  return (
    <div className="rounded-lg border border-[#E8F5E9] p-3 space-y-2" data-testid={`till-today-${s.store}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-[#1B5E20] text-sm">{s.store_name}</p>
        <span className="text-[10px] text-slate-400">{fmtHours(s.hours.open)}–{fmtHours(s.hours.close)}</span>
      </div>
      <p className={`text-xs font-medium flex items-center gap-1.5 ${headline.cls}`} data-testid={`till-today-${s.store}-status`}>
        <Icon className="w-3.5 h-3.5" /> {headline.text}
      </p>
      {s.is_open && (
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div><p className="text-slate-400">Float</p><p className="font-bold">{money(s.opening_float)}</p></div>
          <div><p className="text-slate-400 whitespace-nowrap">Takings</p><p className="font-bold text-emerald-700">{money(s.cash_sales - (s.cash_refunds || 0))}</p></div>
          <div><p className="text-slate-400">Expected</p><p className="font-bold">{money(s.expected_cash)}</p></div>
        </div>
      )}
      {last && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div><p className="text-slate-400 whitespace-nowrap">Takings</p><p className="font-bold text-emerald-700">{money((last.cash_sales || 0) - (last.cash_refunds || 0))}</p></div>
            <div><p className="text-slate-400">Expected</p><p className="font-bold">{money(last.expected_cash)}</p></div>
            <div><p className="text-slate-400">Counted</p><p className="font-bold">{money(last.counted_cash)}</p></div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <VarianceBadge v={last.variance} />
            {s.closed_today.length > 1 && <span className="text-[10px] text-slate-400">{s.closed_today.length} closes today</span>}
          </div>
          {last.closing_notes && <p className="text-xs text-slate-600 italic" data-testid={`till-today-${s.store}-reason`}>“{last.closing_notes}”</p>}
        </div>
      )}
    </div>
  );
};

// Owner's view of every store's till routine today.
export const TillTodayCard = () => {
  const { getAuthHeader, token } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/till/today`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (_) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const id = setInterval(load, 120000);
    window.addEventListener(TILL_CHANGED_EVENT, load);
    return () => { clearInterval(id); window.removeEventListener(TILL_CHANGED_EVENT, load); };
  }, [load]);

  if (!data) return null;
  return (
    <Card className="bg-white border border-[#E8F5E9] rounded-xl shadow-sm" data-testid="till-today-card">
      <CardHeader className="pb-2 border-b border-[#E8F5E9] flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold text-[#1B5E20] flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          <Banknote className="w-4 h-4" /> Tills today
        </CardTitle>
        <Link to="/till" className="text-xs text-[#558B2F] underline">Till register</Link>
      </CardHeader>
      <CardContent className="pt-3 space-y-3">
        {data.stores.map(s => <StoreTill key={s.store} s={s} />)}
      </CardContent>
    </Card>
  );
};
